import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Users, Search, ArrowLeft, Clock, Star, ShieldCheck, BadgeCheck, Palette } from "lucide-react";

/**
 * /admin/aktivitet – oversikt over sist innloggede brukere.
 * Krever `users.view` permission (håndteres av backend).
 */

const WINDOWS = [
  { key: "24h", label: "Siste 24t" },
  { key: "7d",  label: "Siste 7 dager" },
  { key: "30d", label: "Siste 30 dager" },
  { key: "all", label: "Alle" },
];

const CATEGORY_FILTERS = [
  { key: "all",      label: "Alle roller" },
  { key: "vip",      label: "VIP" },
  { key: "verified", label: "Verifiserte" },
  { key: "seller",   label: "Selgere" },
  { key: "creator",  label: "Skapere" },
  { key: "admin",    label: "Admin/Staff" },
];

const GENDER_SYMBOL = {
  mann:   { s: "♂", title: "Mann",   cls: "text-blue-300" },
  kvinne: { s: "♀", title: "Kvinne", cls: "text-pink-300" },
  trans:  { s: "⚧", title: "Trans",  cls: "text-purple-300" },
  annet:  { s: "◇", title: "Annet",  cls: "text-zinc-400" },
};

function CategoryBadges({ u }) {
  const badges = [];
  const isCouple = Boolean(u.couple_id || u.linked_couple_id) || u.role === "couple";
  if (isCouple) {
    badges.push(<span key="par" title="Par" className="text-rose-300">💑</span>);
  } else if (u.gender && GENDER_SYMBOL[u.gender]) {
    const g = GENDER_SYMBOL[u.gender];
    badges.push(<span key="g" title={g.title} className={g.cls}>{g.s}</span>);
  }
  if (u.is_vip) {
    badges.push(<span key="vip" title="VIP"><Star className="h-3.5 w-3.5 text-[var(--gold)] fill-[var(--gold)]" /></span>);
  }
  if (u.is_verified || (u.verification_level || 0) >= 1) {
    badges.push(<span key="ver" title="Verifisert"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /></span>);
  }
  if (u.seller_verified) {
    badges.push(<span key="sel" title="Selger"><BadgeCheck className="h-3.5 w-3.5 text-[var(--magenta)]" /></span>);
  }
  if (u.is_creator) {
    badges.push(<span key="cr" title="Skaper"><Palette className="h-3.5 w-3.5 text-fuchsia-400" /></span>);
  }
  if (!badges.length) return <span className="text-white/20">—</span>;
  return <span className="inline-flex items-center gap-1.5">{badges}</span>;
}


function relative(iso) {
  if (!iso) return "Aldri";
  const then = new Date(iso).getTime();
  if (!then) return "Aldri";
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s siden`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m siden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}t siden`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d siden`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mnd siden`;
  return `${Math.floor(mo / 12)}år siden`;
}

function fullDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nb-NO", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function ActivityDot({ iso }) {
  if (!iso) return <span className="inline-block h-2 w-2 rounded-full bg-zinc-600" title="Aldri" />;
  const diff = Date.now() - new Date(iso).getTime();
  const min = diff / 60000;
  let cls = "bg-zinc-600";
  let title = "Inaktiv";
  if (min < 5) { cls = "bg-emerald-400"; title = "Online nå"; }
  else if (min < 60) { cls = "bg-emerald-500/60"; title = "Nylig aktiv"; }
  else if (min < 60 * 24) { cls = "bg-amber-400"; title = "Aktiv i dag"; }
  else if (min < 60 * 24 * 7) { cls = "bg-amber-500/60"; title = "Aktiv denne uken"; }
  else { cls = "bg-zinc-500"; title = "Lenge siden"; }
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} title={title} />;
}

export default function AdminAktivitet() {
  const [rows, setRows] = useState([]);
  const [buckets, setBuckets] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [window, setWindow] = useState("all");
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const limit = 50;

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({
        window,
        filter,
        limit: String(limit),
        offset: String(offset),
      });
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/users/activity?${params.toString()}`);
      setRows(r.data?.users || []);
      setBuckets(r.data?.buckets || null);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setErr(e.response?.data?.detail || "Klarte ikke laste aktivitet.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [window, filter, offset]);

  // Søk debouncet manuelt via submit
  const onSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    load();
  };

  const pages = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const current = Math.floor(offset / limit) + 1;
    return { totalPages, current };
  }, [total, offset]);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/board" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3.5 w-3.5" /> Admin-oversikt
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Clock className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Aktivitet</h1>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-6">
          Se når hver bruker sist var innlogget. Sortert etter sist aktivitet.
        </p>

        {/* Bucket-kort */}
        {buckets && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              ["24h", "Siste 24t", buckets["24h"]],
              ["7d",  "Siste 7 dager", buckets["7d"]],
              ["30d", "Siste 30 dager", buckets["30d"]],
              ["never", "Aldri sett", buckets["never"]],
            ].map(([k, label, n]) => (
              <button
                key={k}
                type="button"
                onClick={() => { if (k !== "never") { setWindow(k); setOffset(0); } }}
                className={`text-left panel-tight p-4 border transition ${
                  window === k ? "border-[var(--magenta)] bg-[var(--magenta)]/5" : "border-white/10 hover:border-white/25"
                }`}
                data-testid={`activity-bucket-${k}`}
              >
                <div className="text-xs uppercase tracking-widest text-[var(--cream-soft)]">{label}</div>
                <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{n}</div>
              </button>
            ))}
          </div>
        )}

        {/* Filter-rad – tidsvindu */}
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => { setWindow(w.key); setOffset(0); }}
              className={`px-3 py-1.5 text-xs font-meta uppercase tracking-widest border ${
                window === w.key
                  ? "bg-[var(--magenta)] text-white border-[var(--magenta)]"
                  : "border-white/15 text-[var(--cream-soft)] hover:border-white/40"
              }`}
              data-testid={`filter-${w.key}`}
            >
              {w.label}
            </button>
          ))}
          <form onSubmit={onSearch} className="flex-1 min-w-[220px] flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Søk brukernavn…"
                className="input-editorial w-full pl-8"
                data-testid="activity-search-input"
              />
            </div>
            <button type="submit" className="btn-ghost text-xs" data-testid="activity-search-btn">Søk</button>
          </form>
        </div>

        {/* Filter-rad – kategori */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {CATEGORY_FILTERS.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => { setFilter(c.key); setOffset(0); }}
              className={`px-3 py-1.5 text-[10px] font-meta uppercase tracking-widest border ${
                filter === c.key
                  ? "bg-emerald-500 text-black border-emerald-500"
                  : "border-white/10 text-[var(--cream-soft)] hover:border-white/30"
              }`}
              data-testid={`category-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Tabell */}
        <div className="panel-tight overflow-hidden">
          {err && (
            <div className="p-4 text-red-400 text-sm border-b border-red-400/20 bg-red-500/5">{err}</div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-[var(--cream-soft)] border-b border-white/10">
                <th className="p-3">Bruker</th>
                <th className="p-3 hidden sm:table-cell">Rolle</th>
                <th className="p-3">Sist innlogget</th>
                <th className="p-3 hidden md:table-cell">Registrert</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="p-8 text-center text-[var(--cream-soft)]">Laster…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="p-8 text-center text-[var(--cream-soft)]">Ingen treff.</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`activity-row-${u.username}`}>
                  <td className="p-3">
                    <Link to={`/u/${u.username}`} className="flex items-center gap-2 hover:text-[var(--magenta)]">
                      {u.avatar_url ? (
                        <img src={u.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                      ) : (
                        <div className="h-7 w-7 rounded-full bg-zinc-800 flex items-center justify-center text-xs text-white/60">
                          {u.username?.[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium">{u.username}</span>
                      {u.suspended && (
                        <span className="px-1.5 py-0.5 text-[10px] font-meta uppercase bg-red-500/20 text-red-300 border border-red-500/40">
                          Suspended
                        </span>
                      )}
                    </Link>
                  </td>
                  <td className="p-3 hidden sm:table-cell text-[var(--cream-soft)] text-xs">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>
                        {u.role === "owner" && <span className="text-amber-400 font-meta uppercase tracking-widest">Owner</span>}
                        {u.role === "admin" && <span className="text-amber-300 font-meta uppercase tracking-widest">Admin</span>}
                        {u.role === "staff" && <span className="text-blue-300 font-meta uppercase tracking-widest">Staff</span>}
                        {!u.role && <span className="text-white/40">bruker</span>}
                      </span>
                      <CategoryBadges u={u} />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <ActivityDot iso={u.last_seen} />
                      <div className="flex flex-col leading-tight">
                        <span className="text-[var(--cream)]">{relative(u.last_seen)}</span>
                        <span className="text-[10px] text-[var(--cream-faint)]">{fullDate(u.last_seen)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell text-xs text-[var(--cream-soft)]">
                    {fullDate(u.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginering */}
        {!loading && total > limit && (
          <div className="flex items-center justify-between mt-4 text-xs text-[var(--cream-soft)]">
            <div>
              Side {pages.current} av {pages.totalPages} · {total} brukere
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
                className="btn-ghost disabled:opacity-40"
                data-testid="page-prev"
              >
                Forrige
              </button>
              <button
                type="button"
                disabled={offset + limit >= total}
                onClick={() => setOffset(offset + limit)}
                className="btn-ghost disabled:opacity-40"
                data-testid="page-next"
              >
                Neste
              </button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-[var(--cream-faint)] mt-6 flex items-center gap-1">
          <Users className="h-3 w-3" /> `last_seen` oppdateres ved innlogging og hver aktive forespørsel. Ikke bevis på hva brukeren gjorde – kun tidsstempel.
        </p>
      </div>
    </Layout>
  );
}
